import { ColumnFilter, ColumnFilterMap, FilterOption } from 'util/filtering/models';

export interface ProjectGroupFilterConfig {
  key: string;
  backendField: string;
  type: 'string' | 'enum' | 'exists';
  name: string;
  supportsExclude?: boolean;
  staticOptions?: FilterOption[];
  isDynamic?: boolean;
  transform?: (values: FilterOption[], rule: string) => Record<string, unknown>[];
}

export interface ProjectGroupDynamicFilterOptions {
  projectAssociation?: FilterOption[];
  groupAdmin?: FilterOption[];
}

export interface ProjectGroupFilterBuildResult {
  andConditions: Array<{ or: Array<Record<string, unknown>> }>;
}

export const PROJECT_GROUP_FILTERS: Record<string, ProjectGroupFilterConfig> = {
  adminStatus: {
    key: 'adminStatus',
    backendField: 'hasGroupAdmin',
    type: 'exists',
    name: 'Admin status',
    staticOptions: [
      { id: 'assigned', label: 'Group admin assigned' },
      { id: 'not_assigned', label: 'No group admin assigned' },
    ],
    transform: (values: FilterOption[], rule: string) => {
      if (rule === 'allExcept') {
        const allOptions = ['assigned', 'not_assigned'];
        const selectedIds = new Set(values.map((v) => v.id));
        const remainingOptions = allOptions.filter((id) => !selectedIds.has(id));

        return remainingOptions.map((id) => {
          if (id === 'assigned') return { hasGroupAdmin: { eq: true } };
          if (id === 'not_assigned') return { hasGroupAdmin: { eq: false } };
          return {};
        });
      }

      return values.map((v) => {
        if (v.id === 'assigned') return { hasGroupAdmin: { eq: true } };
        if (v.id === 'not_assigned') return { hasGroupAdmin: { eq: false } };
        return {};
      });
    },
  },
  spendPolicy: {
    key: 'spendPolicy',
    backendField: 'isMonthlyBudgetUnlimited',
    type: 'exists',
    name: 'Spend policy',
    staticOptions: [
      { id: 'unlimited', label: 'Unlimited spending' },
      { id: 'capped', label: 'Capped (limit set)' },
    ],
    transform: (values: FilterOption[], rule: string) => {
      if (rule === 'allExcept') {
        const allOptions = ['unlimited', 'capped'];
        const selectedIds = new Set(values.map((v) => v.id));
        const remainingOptions = allOptions.filter((id) => !selectedIds.has(id));

        return remainingOptions.map((id) => {
          if (id === 'unlimited') return { isMonthlyBudgetUnlimited: { eq: true } };
          if (id === 'capped') return { isMonthlyBudgetUnlimited: { eq: false } };
          return {};
        });
      }

      return values.map((v) => {
        if (v.id === 'unlimited') return { isMonthlyBudgetUnlimited: { eq: true } };
        if (v.id === 'capped') return { isMonthlyBudgetUnlimited: { eq: false } };
        return {};
      });
    },
  },
  projectAssociation: {
    key: 'projectAssociation',
    backendField: 'projectAssociation',
    type: 'string',
    name: 'Project association',
    supportsExclude: true,
    isDynamic: true,
    transform: (values: FilterOption[], rule: string) => {
      const operator = rule === 'noExcept' ? 'contains' : 'notContains';
      return values.map((v) => ({
        projectAssociation: { [operator]: v.id },
      }));
    },
  },
  groupAdmin: {
    key: 'groupAdmin',
    backendField: 'groupAdminId',
    type: 'string',
    name: 'Group admin',
    supportsExclude: true,
    isDynamic: true,
    transform: (values: FilterOption[], rule: string) => {
      const operator = rule === 'noExcept' ? 'contains' : 'notContains';
      return values.map((v) => ({
        groupAdminId: { [operator]: v.id },
      }));
    },
  },
};

const buildDefaultConditions = (
  filter: ColumnFilter,
  config: ProjectGroupFilterConfig
): Record<string, unknown>[] => {
  const rule = filter.rule || 'noExcept';
  const operator = rule === 'noExcept' ? 'eq' : 'ne';
  const selectedValues = filter.selectedValues || [];

  return selectedValues.map((value) => ({
    [config.backendField]: { [operator]: value.id },
  }));
};

const isExclusionCondition = (condition: Record<string, unknown>): boolean => {
  return Object.values(condition).some((value) => {
    if (typeof value === 'object' && value !== null) {
      return 'notContains' in value || 'ne' in value;
    }
    return false;
  });
};

export const buildProjectGroupFilterQuery = (
  columnFilters: ColumnFilterMap
): ProjectGroupFilterBuildResult => {
  const andConditions: Array<{ or: Array<Record<string, unknown>> }> = [];

  Object.entries(columnFilters).forEach(([key, filter]) => {
    if (!filter.selectedValues?.length) return;

    const config = PROJECT_GROUP_FILTERS[key];
    if (!config) return;

    const rule = filter.rule || 'noExcept';
    const conditions = config.transform
      ? config.transform(filter.selectedValues, rule)
      : buildDefaultConditions(filter, config);

    const validConditions = conditions.filter((c) => Object.keys(c).length > 0);
    if (validConditions.length > 0) {
      const hasExclusionOperator = validConditions.some(isExclusionCondition);

      if (hasExclusionOperator) {
        validConditions.forEach((condition) => {
          andConditions.push({ or: [condition] });
        });
      } else {
        andConditions.push({ or: validConditions });
      }
    }
  });

  return { andConditions };
};

export const generateProjectGroupFilterObject = (columnFilters: ColumnFilterMap) => {
  const { andConditions } = buildProjectGroupFilterQuery(columnFilters);

  if (andConditions.length === 0) {
    return undefined;
  }

  return {
    and: andConditions,
  };
};

const mapConfigTypeToColumnFilterType = (
  configType: ProjectGroupFilterConfig['type']
): 'string' | 'number' | 'date' => {
  if (configType === 'exists' || configType === 'enum') {
    return 'string';
  }
  return configType;
};

export const buildProjectGroupColumnFiltersFromConfig = (
  pageName: string,
  dynamicOptions?: ProjectGroupDynamicFilterOptions
): ColumnFilterMap => {
  return Object.fromEntries(
    Object.entries(PROJECT_GROUP_FILTERS).map(([key, config]) => {
      let options: FilterOption[] = config.staticOptions || [];

      if (config.isDynamic && dynamicOptions) {
        const dynamicOpts = dynamicOptions[key as keyof ProjectGroupDynamicFilterOptions];
        if (dynamicOpts) {
          options = dynamicOpts;
        }
      }

      return [
        key,
        {
          key: config.key,
          type: mapConfigTypeToColumnFilterType(config.type),
          name: config.name,
          rule: 'noExcept',
          page: pageName,
          options,
          selectedValues: [],
        } as ColumnFilter,
      ];
    })
  );
};
